import { html, nothing, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import style from './contributors-detractors.scss?inline';
import type {
  ContributorsDetractors,
  ContributorsDetractorsItem,
} from './contributors-detractors.model';
import { Constants, DISPLAY_TYPE } from './contributors-detractors.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import {
  dateShort,
  dateStringSplit,
  dateToMMDDYYYY,
} from '../../common/utils/formatDate';
import { toLowerCase } from '../../common/utils/textFormat';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { ContributorsDetractorsFragments } from '../../common/fragments/contributors-detractors.fragments.js';

/**
 * @tag pop-contributors-detractors-component
 * @property {ContributorsDetractors} [componentObject] Contributors and Detractors content
 * @attribute {ContributorsDetractors} [data-component-object] Contributors and Detractors content
 */
@customElement(Constants.TAG_NAME)
export class PopContributorsDetractorsComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: ContributorsDetractors;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopContributorsDetractorsComponent.tagName;

  /**
   * @internal
   */
  fragment = ContributorsDetractorsFragments.contributorsDetractorsFragment;

  /**
   * @internal
   */
  @state()
  period: string = Constants.DEFAULT_PERIOD;

  @state()
  selectedAsOfDate: string | undefined = '';

  @state()
  selectedTab: string = Constants.DEFAULT_TAB;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = _response?.contributorsDetractorsExhibit;
  }

  initialize(): void {
    super.initialize();

    if (!this.selectedAsOfDate) {
      this.initializeAsOfDate();
      this.initializePeriod();
    }
  }

  private initializeAsOfDate(): void {
    this.selectedAsOfDate = this.componentObject?.asOfDate;
  }

  private initializePeriod(): void {
    const contributorsList = this.componentObject?.contributorsList ?? [];
    const detractorsList = this.componentObject?.detractorsList ?? [];

    const availableDataPeriods = this.getAvailableDataPeriods();

    if (
      (contributorsList?.length || detractorsList?.length) &&
      !availableDataPeriods?.includes(this.period)
    ) {
      this.period = availableDataPeriods[0];
    }
  }

  private getAvailableDataPeriods(): string[] {
    const uniquePeriods = new Set<string>();

    const contributorsList = this.componentObject?.contributorsList ?? [];
    const detractorsList = this.componentObject?.detractorsList ?? [];

    contributorsList.forEach((period) => {
      uniquePeriods.add(period.timePeriodName);
    });

    detractorsList.forEach((period) => {
      uniquePeriods.add(period.timePeriodName);
    });

    return Array.from(uniquePeriods);
  }

  private refreshData(): void {
    this.callApiService({
      historicalDates: { contributorsDetractors: [this.selectedAsOfDate] },
    });
  }

  private renderTopSection() {
    return html`
      <h3
        class="contributors-detractors-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderControlSection() {
    return html`
      <div
        class="contributors-detractors-controls b-margin-block-start--micro--jumbo"
      >
        ${this.renderAsOfDate()} ${this.renderViewPeriod()}
      </div>
    `;
  }

  private renderAsOfDate() {
    const availableDates = this.componentObject?.availablePriorDates;

    return html`
      ${availableDates && availableDates.length === 1
        ? html` <p
            class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
          >
            ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
            ${dateShort(availableDates[0])}
          </p>`
        : nothing}
      ${availableDates && availableDates.length > 1
        ? html`<beacon-control>
            <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}"
              >${Constants.DATA_AS_OF_DROPDOWN_LABEL}</label
            >
            <select
              id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
              @change="${this.asOfDateChangeHandler}"
            >
              ${availableDates.map(
                (date) =>
                  html`<option
                    value="${date}"
                    ?selected=${date === this.selectedAsOfDate}
                  >
                    ${dateToMMDDYYYY(date)}
                  </option>`,
              )}
            </select>
          </beacon-control> `
        : nothing}
    `;
  }

  private asOfDateChangeHandler(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      this.selectedAsOfDate = dateStringSplit(event.target?.value);
      this.refreshData();
    }
  }

  renderViewPeriod() {
    const availableDataPeriods = this.getAvailableDataPeriods();

    if (availableDataPeriods.length === 0) {
      return nothing;
    }

    if (availableDataPeriods.length === 1) {
      return html`<p class="b-typography-sans-serif--caption-loose--300">
        ${Constants.PERIOD_NO_DROPDOWN_PREFIX_LABEL} ${toLowerCase(this.period)}
        ${Constants.PERIOD_NO_DROPDOWN_SUFFIX_LABEL}
      </p>`;
    }

    return html` <beacon-control>
      <label slot="label" for="${Constants.PERIOD_DROPDOWN_ID}"
        >${Constants.PERIOD_DROPDOWN_LABEL}</label
      >
      <select
        id="${Constants.PERIOD_DROPDOWN_ID}"
        @change="${this.periodChangeHandler}"
      >
        ${availableDataPeriods.map((period) => {
          return html`
            <option value="${period}" ?selected=${period === this.period}>
              ${toLowerCase(period)}
            </option>
          `;
        })}
      </select>
    </beacon-control>`;
  }

  private periodChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value) {
      this.period = value;
    } else {
      console.warn(`Invalid period value: ${value}`);
    }
  }

  private renderTabSection() {
    return html`
      <beacon-tabs variant="secondary" tab-type="standard">
        <beacon-tab
          slot="tab-children"
          label="${Constants.CONTRIBUTORS_TAB_LABEL}"
          @beacon-event-click="${() => {
            this.selectedTab = DISPLAY_TYPE.CONTRIBUTORS;
          }}"
        ></beacon-tab>
        <beacon-tab
          slot="tab-children"
          label="${Constants.DETRACTORS_TAB_LABEL}"
          @beacon-event-click="${() => {
            this.selectedTab = DISPLAY_TYPE.DETRACTORS;
          }}"
        ></beacon-tab>
        <beacon-tab-panel slot="tab-panel">
          ${this.renderContributorsTab()}
        </beacon-tab-panel>
        <beacon-tab-panel slot="tab-panel">
          ${this.renderDetractorsTab()}
        </beacon-tab-panel>
      </beacon-tabs>
    `;
  }

  private renderContributorsTab() {
    //The condition checked here is being used as a workaround to ensure
    //that the chart renders correctly. Without this, the chart may be
    //sized incorrectly due to some interaction between the tabs and the
    //chart.
    if (this.selectedTab !== DISPLAY_TYPE.CONTRIBUTORS) {
      return nothing;
    }
    const itemList = this.getContributorPeriodData() || [];

    return html`
      ${itemList.length === 0
        ? this.renderNoDataMessage(Constants.CONTRIBUTORS_NO_DATA_MESSAGE)
        : html`
            ${this.renderChartSection(DISPLAY_TYPE.CONTRIBUTORS, itemList)}
            ${this.renderTableSection(DISPLAY_TYPE.CONTRIBUTORS, itemList)}
          `}
    `;
  }

  private getContributorPeriodData(): ContributorsDetractorsItem[] | undefined {
    return this.componentObject?.contributorsList?.find(
      (period) => period.timePeriodName === this.period,
    )?.values;
  }

  private renderDetractorsTab() {
    //The condition checked here is being used as a workaround to ensure
    //that the chart renders correctly. Without this, the chart may be
    //sized incorrectly due to some interaction between the tabs and the
    //chart.
    if (this.selectedTab !== DISPLAY_TYPE.DETRACTORS) {
      return nothing;
    }
    const itemList = this.getDetractorPeriodData() || [];

    return html`
      ${itemList.length === 0
        ? this.renderNoDataMessage(Constants.DETRACTORS_NO_DATA_MESSAGE)
        : html`
            ${this.renderChartSection(DISPLAY_TYPE.DETRACTORS, itemList)}
            ${this.renderTableSection(DISPLAY_TYPE.DETRACTORS, itemList)}
          `}
    `;
  }

  private getDetractorPeriodData(): ContributorsDetractorsItem[] | undefined {
    return this.componentObject?.detractorsList?.find(
      (period) => period.timePeriodName === this.period,
    )?.values;
  }

  private renderNoDataMessage(message: string) {
    return html`<pop-no-data-available-component
      .message="${message}"
    ></pop-no-data-available-component>`;
  }

  private renderChartSection(
    itemType: string,
    itemList: ContributorsDetractorsItem[],
  ) {
    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-contributors-detractors-chart-component
          data-component-object="${JSON.stringify({
            itemType: itemType,
            itemList: itemList,
            maxPointValue: this.getChartMaxPointValue(),
          })}"
        ></pop-contributors-detractors-chart-component>
      </div>
    `;
  }

  getChartMaxPointValue() {
    const contributors = this.getContributorPeriodData() || [];
    const detractors = this.getDetractorPeriodData() || [];

    const contributorsMax = Math.max(
      ...contributors.map((contributor) => contributor.percentTotalNetAssets),
    );
    const detractorsMax = Math.max(
      ...detractors.map((detractor) => detractor.percentTotalNetAssets),
    );

    return Math.max(contributorsMax, detractorsMax);
  }

  private renderTableSection(
    itemType: string,
    itemList?: ContributorsDetractorsItem[],
  ) {
    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-contributors-detractors-table-component
          data-component-object="${JSON.stringify({
            itemType: itemType,
            itemList: itemList,
          })}"
        ></pop-contributors-detractors-table-component>
      </div>
    `;
  }

  private renderDisclaimerSection() {
    const disclaimers = this.componentObject?.disclaimers ?? [];

    if (disclaimers.length === 0) {
      return nothing;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    return this.renderComponent(
      html` <section class="contributors-detractors-content b-container">
        ${this.renderTopSection()} ${this.renderControlSection()}
        ${this.renderTabSection()} ${this.renderDisclaimerSection()}
      </section>`,
    );
  }
}
